import type { FunctionArgs, FunctionReference, FunctionReturnType } from "convex/server";
import { useEffect, useState } from "react";
import { getConvexClient, isConvexConfigured } from "@/lib/convex";

export type ResilientQueryState<T> = { status: "loading" } | { status: "ready"; value: T } | { status: "error" };

export function useResilientQuery<Query extends FunctionReference<"query">>(query: Query, args: FunctionArgs<Query> | "skip"): ResilientQueryState<FunctionReturnType<Query>> {
  const argsKey = args === "skip" ? "skip" : JSON.stringify(args);
  const [state, setState] = useState<{ key: string; result: ResilientQueryState<FunctionReturnType<Query>> }>({ key: argsKey, result: { status: "loading" } });

  useEffect(() => {
    if (args === "skip" || !isConvexConfigured) return;
    const client = getConvexClient();
    if (!client) return;
    const watch = client.watchQuery(query, args);
    let reported = false;
    const read = () => {
      try {
        const value = watch.localQueryResult();
        if (value !== undefined) setState({ key: argsKey, result: { status: "ready", value } });
      } catch (error) {
        if (!reported) console.warn("[useResilientQuery] query failed, keeping the fallback:", error);
        reported = true;
        setState((current) => (current.key === argsKey && current.result.status === "error" ? current : { key: argsKey, result: { status: "error" } }));
      }
    };
    read();
    return watch.onUpdate(read);
  }, [query, argsKey]);

  if (args === "skip") return { status: "loading" };
  return state.key === argsKey ? state.result : { status: "loading" };
}
